/**
 * Agent mode: the whole card is the conversation.
 *
 * Same hook, same transcript, same Apply button as the rail — what changes is
 * the frame. Threads get a column of their own instead of hiding behind a
 * History button, the message column is centred and capped at a readable
 * measure, and the composer sits in the middle of the screen before the first
 * message rather than pinned to the bottom of an empty panel.
 *
 * That last one is not decoration. An empty chat with the input at the bottom
 * reads as "waiting for something to load"; the same input centred under a
 * prompt reads as "ask me". It is the difference between a blank page and an
 * invitation.
 */

import { useState } from 'react';
import { Coins, History, Plus } from 'lucide-react';
import { Button } from '@/components/shadcn/button';
import { SidebarTrigger } from '@/components/shadcn/sidebar';
import { useI18n } from '@/contexts/I18nContext';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { useAgentChat } from './useAgentChat';
import { MessageList } from './MessageList';
import { Composer } from './Composer';
import { ConversationList } from './ConversationList';

export function AgentWorkspace() {
  const { t, lang } = useI18n();
  const { org } = useAuth();
  const ar = lang === 'ar';
  const locale = ar ? 'ar-BH-u-nu-latn' : 'en';

  const chat = useAgentChat();
  const [draft, setDraft] = useState('');
  /*
    Closed by default.

    It opened to a full-height list of every conversation ever had, which is
    half the screen given to a thing nobody had asked for — the reason to be
    here is the conversation in front of you, not the archive. It is one click
    away and remembers nothing, because a panel that reopens itself is a panel
    you close twice.
  */
  const [threadsOpen, setThreadsOpen] = useState(false);

  const empty = chat.messages.length === 0 && !chat.loadingThread;

  const submit = () => {
    const text = draft;
    setDraft('');
    void chat.send(text);
  };

  const composer = (
    <Composer
      value={draft}
      onChange={setDraft}
      onSend={submit}
      onStop={chat.stop}
      pending={chat.pending}
      files={chat.files}
      onAddFiles={chat.addFiles}
      onRemoveFile={chat.removeFile}
      model={chat.model}
      onModelChange={chat.setModel}
      thinking={chat.thinking}
      onThinkingChange={chat.setThinking}
      autoFocus
    />
  );

  return (
    <div className="flex h-full min-h-0">
      {/* ---- threads ---- */}
      <aside
        className={cn(
          'flex shrink-0 flex-col overflow-hidden border-e bg-sidebar/40',
          'motion-safe:transition-[width] motion-safe:duration-200',
          threadsOpen ? 'w-64' : 'w-0 border-e-0',
          // On a phone there is no room for two columns; threads become a
          // toggle rather than a permanent fixture.
          'max-md:absolute max-md:inset-y-0 max-md:start-0 max-md:z-20 max-md:bg-sidebar',
        )}
        aria-hidden={!threadsOpen}
        inert={!threadsOpen || undefined}
      >
        <div className="w-64">
          <div className="flex items-center gap-1 p-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 justify-start gap-2"
              onClick={() => {
                chat.newConversation();
                setDraft('');
              }}
            >
              <Plus className="size-3.5" />
              {t('agent_new')}
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
            <ConversationList activeId={chat.conversationId} onPick={(id) => void chat.openConversation(id)} />
          </div>
        </div>
      </aside>

      {/* ---- the conversation ---- */}
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-1 px-3">
          {/*
            THE WAY BACK TO THE REST OF THE APP, on a phone.

            Agent mode replaces the card's contents, and the card's contents are
            where Topbar lives — which is the only place the sidebar trigger
            existed. On a laptop that goes unnoticed because the navigation rail
            is still sitting there beside the card. On a phone the rail is a
            sheet with no handle, so anybody who opened the assistant was stuck
            in it: no menu, no way to any other page.

            `md:hidden` because above that width the rail is visible and a second
            control for it would be clutter.
          */}
          <SidebarTrigger className="size-8 text-muted-foreground md:hidden" />
          <span className="mx-1 h-4 w-px bg-border md:hidden" aria-hidden />
          {/*
            Two buttons, not a column. History and "new chat" are things you
            reach for occasionally; giving either of them permanent screen is
            paying rent for a visitor.
          */}
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('agent_threads')}
            title={t('agent_threads')}
            data-active={threadsOpen || undefined}
            className="data-[active]:bg-accent"
            onClick={() => setThreadsOpen((v) => !v)}
          >
            <History className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('agent_new')}
            title={t('agent_new')}
            onClick={() => {
              chat.newConversation();
              setDraft('');
              setThreadsOpen(false);
            }}
          >
            <Plus className="size-4" />
          </Button>
          <span className="mx-1 h-4 w-px bg-border" aria-hidden />

          {org ? (
            <span dir="auto" className="truncate text-[12.5px] text-muted-foreground">
              {t('agent_scope').replace('{org}', (ar ? org.name_ar : null) ?? org.name)}
            </span>
          ) : null}

          {chat.sessionCredits > 0 ? (
            <span className="ms-auto inline-flex items-center gap-1 text-[11px] tabular-nums text-muted-foreground">
              <Coins className="size-3" aria-hidden />
              {chat.sessionCredits.toLocaleString(locale)}
            </span>
          ) : null}
        </header>

        {empty ? (
          /*
            Before the first message the composer is CENTRED, not docked. See
            the note at the top of this file: a docked input on an empty page
            reads as a page that has not finished loading.
          */
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 pb-16">
            <h1 className="text-xl font-semibold">{t('agent_greeting')}</h1>
            <p className="mt-1.5 text-[12.5px] text-muted-foreground">{t('agent_greeting_hint')}</p>
            <div className="mt-6 w-full max-w-2xl">{composer}</div>
          </div>
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-6">
              <MessageList
                wide
                messages={chat.messages}
                decided={chat.decided}
                onDecide={chat.decide}
                error={chat.error}
              />
            </div>
            <div className="shrink-0 px-4 pb-4">
              <div className="mx-auto w-full max-w-3xl">{composer}</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
